import React, { useContext, useState, useEffect } from 'react';
import {
  TelemetryPanelConfig,
  TelemetryUnitSystem,
  TelemetryViewportState,
} from '../types/telemetry-dashboard.types';
import { TelemetryCardKpi } from './TelemetryCardKpi';
import { TelemetryLayoutContext } from './TelemetryLayout';
import { DynamicVirtualizedTelemetryTable } from './DynamicVirtualizedTelemetryTable';
import {
  hasPanelTelemetryData,
  hasPanelDataInWindow,
  resolveTableColumns,
  getBarCategoryCount,
} from '../utils/telemetry-helpers.utils';
import '../styles/telemetry-dashboard-panel.css';

export interface TelemetryCardChartProps {
  panel: TelemetryPanelConfig | any;
  graphType?: string;
  graphMode?: string;
  timeframe?: number;
  precision?: number;
  units?: TelemetryUnitSystem | any;
  viewport?: TelemetryViewportState | any;
  viewportLabel?: string;
  isPaused?: boolean;
  isAtStartBoundary?: boolean;
  onJumpLive?: () => void;
  chartWrapRef?: (el: HTMLElement | null) => void;
  patchWidget?: (id: string, patch: Partial<any>) => void;
  activeMenuTriggerId?: string | null;
  ecInstances?: any;
  isLoadingHistory?: boolean;
  isTelemetryDataLoading?: boolean;
  hasNoData?: boolean;
}

export const TelemetryCardChart: React.FC<TelemetryCardChartProps> = (props) => {
  const layoutCtx = useContext(TelemetryLayoutContext);
  const { panel } = props;

  const mode = props.graphType || props.graphMode || panel.graphMode || 'line';
  const timeframe = props.timeframe ?? layoutCtx?.timeframe ?? 5;
  const precision = props.precision ?? layoutCtx?.precision ?? 2;
  const units = props.units ?? panel.units ?? layoutCtx?.units ?? {};
  const viewport = props.viewport ?? layoutCtx?.viewport ?? { spanMs: 300000, offsetMs: 0 };
  const viewportLabel = props.viewportLabel ?? layoutCtx?.viewportLabel ?? '5 min window · live';
  const isLoadingHistory = props.isLoadingHistory ?? layoutCtx?.isLoadingHistory ?? false;
  const isTelemetryDataLoading = props.isTelemetryDataLoading ?? layoutCtx?.isTelemetryDataLoading ?? false;
  const chartWrapRef = props.chartWrapRef ?? layoutCtx?.getChartWrapRef?.(panel.id);
  const showViewport = mode === 'line' || mode === 'area' || mode === 'cchart';

  const hasConfiguredParameters = Array.isArray(panel.parameters) && panel.parameters.length > 0;
  const hasSeriesData = hasConfiguredParameters ? hasPanelTelemetryData(panel) : false;
  const hasDataInCurrentWindow = hasConfiguredParameters ? hasPanelDataInWindow(panel, timeframe, viewport) : false;
  const showLoadingShimmer = Boolean(hasConfiguredParameters && (isLoadingHistory || (isTelemetryDataLoading && !hasSeriesData)));

  const isNoParameters = !hasConfiguredParameters;

  // Debounced empty state to eliminate any flicker before/after loading & historical fetching
  const [debouncedEmpty, setDebouncedEmpty] = useState(false);

  useEffect(() => {
    // If loading, loading history, or data is present: immediately hide empty state
    if (!hasConfiguredParameters || showLoadingShimmer || isTelemetryDataLoading || isLoadingHistory || hasDataInCurrentWindow) {
      setDebouncedEmpty(false);
      return;
    }

    // Grace period before displaying empty state to allow async store hydration to settle
    const timer = setTimeout(() => {
      setDebouncedEmpty(true);
    }, 280);

    return () => clearTimeout(timer);
  }, [hasConfiguredParameters, showLoadingShimmer, isTelemetryDataLoading, isLoadingHistory, hasDataInCurrentWindow, timeframe, viewport?.offsetMs, viewport?.spanMs]);

  const isTimeframeEmpty = debouncedEmpty;

  const renderContent = () => {
    // KPI Card Renderer
    if (mode === 'kpi') {
      return (
        <TelemetryCardKpi
          panel={panel as any}
          timeframe={timeframe}
          precision={precision}
          units={units as any}
          viewport={viewport as any}
        />
      );
    }

    // Stream Table Renderer
    if (mode === 'ctable') {
      return (
        <DynamicVirtualizedTelemetryTable
          panel={panel}
          mode="ctable"
          units={units}
          precision={precision}
          timeframe={timeframe}
          viewport={viewport}
        />
      );
    }

    // Single Table Renderer
    if (mode === 'table') {
      return (
        <DynamicVirtualizedTelemetryTable
          panel={panel}
          mode="table"
          units={units}
          precision={precision}
          timeframe={timeframe}
          viewport={viewport}
        />
      );
    }

    const resolvedCols = mode === 'gauge' ? resolveTableColumns(panel) : [];
    const totalGauges = resolvedCols.length;
    const isMultiGauge = mode === 'gauge' && totalGauges > 1;
    const gaugeGridCols = totalGauges <= 3 ? totalGauges : (totalGauges === 4 ? 2 : 3);
    const gaugeRowCount = Math.ceil(totalGauges / gaugeGridCols);
    const multiGaugeCanvasHeight = Math.max(gaugeRowCount * 170, 180);

    // Horizontal Bar with many categories -> vertical scroll with fixed bar row height
    const hbarCategoryCount = mode === 'hbar' ? getBarCategoryCount(panel) : 0;
    const isScrollableHBar = mode === 'hbar' && hbarCategoryCount > 6;
    const hbarCanvasHeight = isScrollableHBar ? hbarCategoryCount * 28 + 48 : 0;

    const shouldScrollY = isMultiGauge || isScrollableHBar;
    const canvasMinHeight = isMultiGauge
      ? `${multiGaugeCanvasHeight}px`
      : isScrollableHBar
        ? `${hbarCanvasHeight}px`
        : undefined;

    // ECharts Wrapper (Line, Area, Gauge, Radial, Pie, Bar, Horizontal Bar, Radar, Multi-Chart)
    return (
      <div
        className="telemetry-card-chart-content"
        style={shouldScrollY ? { overflowY: 'auto', overflowX: 'hidden' } : undefined}
      >
        <div
          ref={chartWrapRef as any}
          data-chart-id={panel.id}
          className="telemetry-chart-canvas-wrap"
          style={canvasMinHeight ? { minHeight: canvasMinHeight, height: '100%', width: '100%' } : undefined}
        />
      </div>
    );
  };

  return (
    <div className="telemetry-card-chart-root">
      {renderContent()}

      {/* Live Viewport Overlay Badge */}
      {showViewport && (
        <div className="telemetry-card-viewport-overlay">
          <div className="viewport-badge telemetry-card-viewport-badge" title={viewportLabel}>
            <span>{viewportLabel}</span>
          </div>
        </div>
      )}

      {/* History / Data Hydration Loading Shimmer */}
      {showLoadingShimmer && (
        <div className="widget-skeleton-overlay">
          <div className="widget-skeleton-spinner" />
          <div className="widget-skeleton-text">
            {isLoadingHistory ? 'Fetching Historical Telemetry...' : 'Loading Telemetry Data...'}
          </div>
        </div>
      )}

      {/* State A: Panel has no parameters configured */}
      {isNoParameters && (
        <div className="widget-empty-state">
          <svg className="widget-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" />
          </svg>
          <div className="widget-empty-title">No Parameters Configured</div>
          <div className="widget-empty-subtitle">
            Add or select telemetry parameters from panel configuration to view data.
          </div>
        </div>
      )}

      {/* State B: Panel has parameters, but no data in the selected timeframe */}
      {isTimeframeEmpty && (
        <div className="widget-empty-state">
          <svg className="widget-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="20" x2="18" y2="10" />
            <line x1="12" y1="20" x2="12" y2="4" />
            <line x1="6" y1="20" x2="6" y2="14" />
            <line x1="2" y1="2" x2="22" y2="22" stroke="var(--accent)" strokeWidth="1.8" />
          </svg>
          <div className="widget-empty-title">No Telemetry Data Available</div>
          <div className="widget-empty-subtitle">
            No telemetry packets received for this channel in the selected window.
          </div>
        </div>
      )}
    </div>
  );
};

// TelemetryPanel alias for backwards compatibility
export { TelemetryCardChart as TelemetryPanel };
